/**
 * Permissions Hook
 * 
 * Provides utilities for checking user permissions and filtering navigation
 */

import { useMemo } from 'react';
import { usePermissions } from '@/providers/PermissionsProvider';

// Map navigation item titles/hrefs to permission screen keys
const navItemToPermissionMap: Record<string, string> = {
  // Main navigation items
  'Dashboard': 'dashboard',
  '/dashboard': 'dashboard',
  'Assets': 'assets',
  '/assets': 'assets',
  'Tasks': 'tasks',
  '/tasks': 'tasks',
  'Help & Support': 'help',
  '/help': 'help',
  
  // Transactions
  'Additions': 'transactions_additions',
  '/transactions/additions': 'transactions_additions',
  'Depreciation': 'transactions_depreciation',
  '/transactions/depreciation': 'transactions_depreciation',
  'Revaluation': 'transactions_revaluation',
  '/transactions/revaluation': 'transactions_revaluation',
  
  // Management (removed for now per requirements)
  'Check-In / Check-Out': 'checkinout',
  '/checkinout': 'checkinout',
  'Compliance': 'compliance',
  '/compliance': 'compliance',
  
  // Audits
  'Audit Execution': 'audits_auditor',
  'Audit Management': 'audits_manager',
  '/audits/dashboard': 'audits_manager',
  '/audits/active': 'audits_manager',
  '/audits/scheduled': 'audits_manager',
  '/audits/history': 'audits_manager',
  '/audits/exceptions': 'audits_manager',
  
  // Reports
  'Insights': 'insights',
  '/insights': 'insights',
  'Asset History': 'enquiry',
  '/enquiry': 'enquiry',
  'Audit Trail': 'audit_trail',
  '/audit': 'audit_trail',
  
  // Data Management
  'Import Assets': 'import_assets',
  '/administration/imports': 'import_assets',
  'Import Transactions': 'import_transactions',
  '/administration/import-transactions': 'import_transactions',
  
  // Configuration
  'Locations': 'locations',
  '/administration/locations': 'locations',
  'Categories': 'categories',
  '/administration/categories': 'categories',
  'Equipment': 'equipment',
  '/administration/equipment': 'equipment',
  'Disposal Reasons': 'disposal_reasons',
  '/administration/disposal-reasons': 'disposal_reasons',
  
  // Settings
  'Team Settings': 'team_settings',
  '/administration/teams-permissions': 'team_settings',
  'Feature Settings': 'feature_settings',
  '/settings/features': 'feature_settings',
  'Asset Options': 'asset_options',
  '/settings/asset-options': 'asset_options',
};

// Map parent navigation items to their child permission categories
const parentNavToCategory: Record<string, string[]> = {
  'Transactions': ['transactions_additions', 'transactions_depreciation', 'transactions_revaluation'],
  'Management': ['checkinout', 'compliance'],
  'Audits': ['audits_auditor', 'audits_manager'],
  'Reports': ['insights', 'enquiry', 'audit_trail'],
  'Data Management': ['import_assets', 'import_transactions'],
  'Configuration': ['locations', 'categories', 'equipment', 'disposal_reasons'],
  'Settings': ['team_settings', 'feature_settings', 'asset_options'],
};

export interface NavItem {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  badge?: {
    count: number;
    variant?: 'default' | 'critical';
  };
}

/**
 * Hook to check if user can access a specific navigation item
 */
export function useCanAccessNavItem() {
  const { userPermissions, permissions } = usePermissions();

  return (item: NavItem): boolean => {
    // If no user permissions loaded yet, allow access (will be checked on route)
    if (!userPermissions) return true;
    
    // In local development (including e2b.app), if user has super user team, allow all
    // Also allow all access in temporary all-access mode
    if (userPermissions.teamIds.includes('local_dev_superuser') || 
        userPermissions.teamIds.includes('temp_all_access')) {
      return true;
    }

    // Check by href first
    if (item.href) {
      const screenKey = navItemToPermissionMap[item.href];
      if (screenKey) {
        // Find the permission definition
        const permDef = permissions.find(p => p.screenKey === screenKey);
        if (permDef) {
          return userPermissions.grantedPermissions.includes(permDef.id);
        }
      }
    }

    // Check by title
    const screenKey = navItemToPermissionMap[item.title];
    if (screenKey) {
      const permDef = permissions.find(p => p.screenKey === screenKey);
      if (permDef) {
        return userPermissions.grantedPermissions.includes(permDef.id);
      }
    }

    // For parent items with children, check if any child is accessible
    if (item.children && item.children.length > 0) {
      const childScreenKeys = parentNavToCategory[item.title];
      if (childScreenKeys) {
        return childScreenKeys.some(key => {
          const permDef = permissions.find(p => p.screenKey === key);
          if (permDef) {
            return userPermissions.grantedPermissions.includes(permDef.id);
          }
          return true;
        });
      }
      // If no mapping, check each child individually
      return item.children.some(child => {
        if (child.href) {
          const childScreenKey = navItemToPermissionMap[child.href];
          if (childScreenKey) {
            const permDef = permissions.find(p => p.screenKey === childScreenKey);
            if (permDef) {
              return userPermissions.grantedPermissions.includes(permDef.id);
            }
          }
        }
        return true;
      });
    }

    // Default to allowing access if no permission mapping found
    return true;
  };
}

/**
 * Hook to filter navigation items based on user permissions
 */
export function useFilteredNavigation(navigationItems: NavItem[]): NavItem[] {
  const { userPermissions, permissions, loading } = usePermissions();
  const canAccessNavItem = useCanAccessNavItem();

  return useMemo(() => {
    // While loading, show all items
    if (loading || !userPermissions) return navigationItems;
    
    // In local development (super user) or temporary all-access mode, show all items
    if (userPermissions.teamIds.includes('local_dev_superuser') || 
        userPermissions.teamIds.includes('temp_all_access')) {
      return navigationItems;
    }
    
    // If user has no granted permissions at all, show all items (likely a permissions loading error)
    if (userPermissions.grantedPermissions.length === 0) return navigationItems;

    const filterItems = (items: NavItem[]): NavItem[] => {
      return items
        .filter(item => canAccessNavItem(item))
        .map(item => {
          if (item.children) {
            const filteredChildren = filterItems(item.children);
            // If no children remain after filtering, hide the parent
            if (filteredChildren.length === 0) {
              return null;
            }
            return {
              ...item,
              children: filteredChildren,
            };
          }
          return item;
        })
        .filter((item): item is NavItem => item !== null);
    };

    return filterItems(navigationItems);
  }, [navigationItems, userPermissions, permissions, loading, canAccessNavItem]);
}

/**
 * Hook to check specific sub-permissions for UI element visibility
 */
export function useFeaturePermission(subPermissionKey: string): boolean {
  const { hasSubPermission, loading } = usePermissions();
  
  // While loading, show the feature (will be validated on action)
  if (loading) return true;
  
  return hasSubPermission(subPermissionKey);
}

/**
 * Hook to check if user can perform specific actions on assets
 */
export function useAssetPermissions() {
  const { hasSubPermission, loading } = usePermissions();

  return useMemo(() => ({
    canView: loading || hasSubPermission('view_assets'),
    canCreate: loading || hasSubPermission('create_assets'),
    canEdit: loading || hasSubPermission('edit_assets'),
    canDelete: loading || hasSubPermission('delete_assets'),
  }), [hasSubPermission, loading]);
}

/**
 * Hook to check if user can perform specific actions on tasks
 */
export function useTaskPermissions() {
  const { hasSubPermission, loading } = usePermissions();

  return useMemo(() => ({
    canView: loading || hasSubPermission('view_tasks'),
    canCreate: loading || hasSubPermission('create_tasks'),
    canEdit: loading || hasSubPermission('edit_tasks'),
    canAssign: loading || hasSubPermission('assign_tasks'),
    isAdmin: loading || hasSubPermission('task_administrator'),
  }), [hasSubPermission, loading]);
}

/**
 * Hook to check if user can perform specific actions on audits
 */
export function useAuditPermissions() {
  const { hasSubPermission, hasPermission, loading } = usePermissions();

  return useMemo(() => ({
    // Auditor permissions (mobile app)
    canViewAssignedAudits: loading || hasSubPermission('view_assigned_audits'),
    canCarryOutAudit: loading || hasSubPermission('carry_out_audit'),
    canProcessAudit: loading || hasSubPermission('process_audit'),
    
    // Manager permissions (main app)
    canViewAllAudits: loading || hasSubPermission('view_all_audits'),
    canCreateAudits: loading || hasSubPermission('create_audits'),
    canEditAudits: loading || hasSubPermission('edit_audits'),
    canFlagAudits: loading || hasSubPermission('flag_audits'),
    canRaiseComments: loading || hasSubPermission('raise_comments'),
    canRaiseWorkItems: loading || hasSubPermission('raise_work_items'),
    
    // High-level access
    isAuditor: loading || hasPermission('audits_auditor'),
    isManager: loading || hasPermission('audits_manager'),
  }), [hasSubPermission, hasPermission, loading]);
}

/**
 * Hook to check if user can manage teams and permissions
 */
export function useTeamSettingsPermissions() {
  const { hasSubPermission, loading } = usePermissions();

  return useMemo(() => ({
    canViewTeams: loading || hasSubPermission('view_teams'),
    canManageTeams: loading || hasSubPermission('manage_teams'),
    canManagePermissions: loading || hasSubPermission('manage_permissions'),
  }), [hasSubPermission, loading]);
}
